import { BaseObj } from '@/objects/BaseObj';
import { useBaseObjEntityStateHandler } from '@/objects/entitystatehandler/entitystatehandler';
import { isRef, ref, unref } from 'vue';
import { ParticipantID } from './ParticipantID';
import type { EntityState } from './../enums/EntityState';
import type { Gender } from './../enums/Gender';
import { formatDateIso } from '@/utilities/dateutils';

export class RelayTeamContestant extends BaseObj {
    public ID: number;
    public __id: number;

    public readonly Age: number | null;

    public Birthday: Date | null;
    public __birthday: Date | null;

    public Chip1: string | null;
    public __chip1: string | null;

    public Chip2: string | null;
    public __chip2: string | null;

    public ContestantID: number;
    public __contestantid: number;

    public Email: string | null;
    public __email: string | null;

    public EventID: number;
    public __eventid: number;

    public Firstname: string | null = '';
    public __firstname: string | null = '';

    public Gender: Gender | null;
    public __gender: Gender | null;

    public LegNumber: number | null;
    public __legnumber: number | null;

    public ParticipantID: number | null;
    public __participantid: number | null;

    public ParticipantIDs: ParticipantID[];
    public __participantids: ParticipantID[];

    public Surname: string | null = '';
    public __surname: string | null = '';

    public __entityStateHandler;
    public __selfEntityState: EntityState;
    public __entityState: any;
    public get EntityState(): EntityState {
        return unref(this.__entityState);
    }
    /***
     * @deprecated Use the static FromJsValues method instead of the constructor to get type safety and default values
     */
    public constructor(values: {
        ID?: number;
        Age?: number | null;
        Birthday?: Date | null;
        Chip1?: string | null;
        Chip2?: string | null;
        ContestantID?: number;
        Email?: string | null;
        EntityState?: EntityState;
        EventID?: number;
        Firstname?: string | null;
        Gender?: Gender | null;
        LegNumber?: number | null;
        ParticipantID?: number | null;
        ParticipantIDs?: ParticipantID[];
        Surname?: string | null;
    }) {
        super();
        this.__entityState = ref(values.EntityState ?? 'Unchanged');
        this.__selfEntityState = values.EntityState ?? 'Unchanged';
        this.__entityStateHandler = useBaseObjEntityStateHandler<RelayTeamContestant>(unref(this.__entityState), (states) => {
            if (isRef(this.__entityState)) {
                this.__entityState.value = states.EntityStateWithChildrenConsidered;
            } else {
                console.warn('RelayTeamContestant tried to set entitystate, but entitystate is not a ref! This should never happen');
            }
            this.__selfEntityState = states.SelfEntityState;
        });
        this.ID = values.ID ?? 0;
        this.__id = values.ID ?? 0;
        Object.defineProperty(this, 'ID', {
            get(): number {
                return this.__id;
            },
            enumerable: true,
            configurable: true,
        });

        this.Age = values.Age ?? null;

        this.Birthday = values.Birthday ?? null;
        this.__birthday = values.Birthday ?? null;
        Object.defineProperty(this, 'Birthday', {
            get(): Date | null {
                return this.__birthday;
            },
            set(value: Date | null) {
                this.__entityStateHandler.propertyChanged('Birthday', this.__birthday, value);
                this.__birthday = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.Chip1 = values.Chip1 ?? null;
        this.__chip1 = values.Chip1 ?? null;
        Object.defineProperty(this, 'Chip1', {
            get(): string | null {
                return this.__chip1;
            },
            set(value: string | null) {
                this.__entityStateHandler.propertyChanged('Chip1', this.__chip1, value);
                this.__chip1 = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.Chip2 = values.Chip2 ?? null;
        this.__chip2 = values.Chip2 ?? null;
        Object.defineProperty(this, 'Chip2', {
            get(): string | null {
                return this.__chip2;
            },
            set(value: string | null) {
                this.__entityStateHandler.propertyChanged('Chip2', this.__chip2, value);
                this.__chip2 = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.ContestantID = values.ContestantID ?? 0;
        this.__contestantid = values.ContestantID ?? 0;
        Object.defineProperty(this, 'ContestantID', {
            get(): number {
                return this.__contestantid;
            },
            set(value: number) {
                this.__entityStateHandler.propertyChanged('ContestantID', this.__contestantid, value);
                this.__contestantid = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.Email = values.Email ?? null;
        this.__email = values.Email ?? null;
        Object.defineProperty(this, 'Email', {
            get(): string | null {
                return this.__email;
            },
            set(value: string | null) {
                this.__entityStateHandler.propertyChanged('Email', this.__email, value);
                this.__email = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.EventID = values.EventID ?? 0;
        this.__eventid = values.EventID ?? 0;
        Object.defineProperty(this, 'EventID', {
            get(): number {
                return this.__eventid;
            },
            set(value: number) {
                this.__entityStateHandler.propertyChanged('EventID', this.__eventid, value);
                this.__eventid = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.Firstname = values.Firstname ?? '';
        this.__firstname = values.Firstname ?? '';
        Object.defineProperty(this, 'Firstname', {
            get(): string | null {
                return this.__firstname;
            },
            set(value: string | null) {
                this.__entityStateHandler.propertyChanged('Firstname', this.__firstname, value);
                this.__firstname = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.Gender = values.Gender ?? 'm';
        this.__gender = values.Gender ?? 'm';
        Object.defineProperty(this, 'Gender', {
            get(): Gender | null {
                return this.__gender;
            },
            set(value: Gender | null) {
                this.__entityStateHandler.propertyChanged('Gender', this.__gender, value);
                this.__gender = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.LegNumber = values.LegNumber ?? null;
        this.__legnumber = values.LegNumber ?? null;
        Object.defineProperty(this, 'LegNumber', {
            get(): number | null {
                return this.__legnumber;
            },
            set(value: number | null) {
                this.__entityStateHandler.propertyChanged('LegNumber', this.__legnumber, value);
                this.__legnumber = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.ParticipantID = values.ParticipantID ?? null;
        this.__participantid = values.ParticipantID ?? null;
        Object.defineProperty(this, 'ParticipantID', {
            get(): number | null {
                return this.__participantid;
            },
            set(value: number | null) {
                this.__entityStateHandler.propertyChanged('ParticipantID', this.__participantid, value);
                this.__participantid = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.ParticipantIDs = values.ParticipantIDs ?? [];
        this.__participantids = this.__entityStateHandler.createArrayWithMutationCallback({
            initalValue: values.ParticipantIDs ?? [],
            callback: (_oldVal, newVal) => this.__entityStateHandler.childArrayOfBaseObjsChanged('ParticipantIDs', newVal),
        });
        this.__participantids.forEach((child) => {
            child._setNotifyParentOfModificationCallback(() =>
                this.__entityStateHandler.childArrayOfBaseObjsChanged('ParticipantIDs', this.ParticipantIDs),
            );
        });
        Object.defineProperty(this, 'ParticipantIDs', {
            get(): ParticipantID[] {
                return this.__participantids;
            },
            set(value: ParticipantID[]) {
                // TODO: Here we could validate that the new and the old value has all the same objects. If we dont we have a bug
                this.__participantids = this.__entityStateHandler.createArrayWithMutationCallback({
                    initalValue: value ?? [],
                    callback: (_oldVal: ParticipantID[], newVal: ParticipantID[]) =>
                        this.__entityStateHandler.childArrayOfBaseObjsChanged('ParticipantIDs', newVal),
                });
                this.__participantids.forEach((child: ParticipantID) => {
                    child._setNotifyParentOfModificationCallback(() =>
                        this.__entityStateHandler.childArrayOfBaseObjsChanged('ParticipantIDs', this.ParticipantIDs),
                    );
                });
                this.__entityStateHandler.childArrayOfBaseObjsChanged('ParticipantIDs', this.ParticipantIDs);
            },
            enumerable: true,
            configurable: true,
        });

        this.Surname = values.Surname ?? '';
        this.__surname = values.Surname ?? '';
        Object.defineProperty(this, 'Surname', {
            get(): string | null {
                return this.__surname;
            },
            set(value: string | null) {
                this.__entityStateHandler.propertyChanged('Surname', this.__surname, value);
                this.__surname = value;
            },
            enumerable: true,
            configurable: true,
        });
    }

    public _setNotifyParentOfModificationCallback(callback: () => void) {
        this.__entityStateHandler.setNotifyParentOfModificationCallback(callback);
    }

    public serialize(): string {
        return (
            '{' +
            `"ID": ${JSON.stringify(this.ID)}` +
            ',' +
            `"Age": ${JSON.stringify(this.Age)}` +
            ',' +
            `"Birthday": ${this.Birthday ? '"' + formatDateIso(this.Birthday).substring(0, 10) + '"' : null}` +
            ',' +
            `"Chip1": ${JSON.stringify(this.Chip1)}` +
            ',' +
            `"Chip2": ${JSON.stringify(this.Chip2)}` +
            ',' +
            `"ContestantID": ${JSON.stringify(this.ContestantID)}` +
            ',' +
            `"Email": ${JSON.stringify(this.Email)}` +
            ',' +
            `"EntityState": ${unref(this.__selfEntityState) == 'Unchanged' ? 0 : unref(this.__selfEntityState) == 'Deleted' ? 1 : unref(this.__selfEntityState) == 'Modified' ? 2 : unref(this.__selfEntityState) == 'Added' ? 3 : 0}` +
            ',' +
            `"EventID": ${JSON.stringify(this.EventID)}` +
            ',' +
            `"Firstname": ${JSON.stringify(this.Firstname)}` +
            ',' +
            `"Gender": ${this.Gender == null ? null : this.Gender == 'm' ? 0 : this.Gender == 'f' ? 1 : this.Gender == 'n' ? 2 : null}` +
            ',' +
            `"LegNumber": ${JSON.stringify(this.LegNumber)}` +
            ',' +
            `"ParticipantID": ${JSON.stringify(this.ParticipantID)}` +
            ',' +
            `"ParticipantIDs": [ ${this.ParticipantIDs.map((x) => (!x ? null : x?.serialize()))} ]` +
            ',' +
            `"Surname": ${JSON.stringify(this.Surname)}` +
            ',' +
            `"ChangedColumns": ${unref(this.__selfEntityState) !== 'Modified' ? 'null' : JSON.stringify(this.__entityStateHandler.getChangedPropertiesArray())}` +
            '}'
        );
    }

    /**
     * Sets the entity state to Deleted
     */
    public Delete() {
        if (this.__selfEntityState === 'Added') {
            throw new Error('Cannot delete an object that is in Added state. It has not been saved yet.');
        }
        this.__selfEntityState = 'Deleted';
        this.__entityStateHandler.entityStateChanged(this.__selfEntityState);
    }

    /**
     * Undeletes the object by setting resetting the EntityState
     *
     * If the object has modifications, the new state will be 'Modified', otherwise 'Unchanged'
     */
    public DeleteUndo() {
        this.__selfEntityState = 'Unchanged';
        this.__entityStateHandler.entityStateChanged(this.__selfEntityState);
    }

    /**
     * Sets the entity state to Added
     *
     * Also sets the ID to the supplied ID. If no id parameter supplied, the previous id is kept
     *
     * Note: This does not do anything with the children, so use with caution
     *
     * The use case of this is to make copies of existing objects as new objects without having to fill every field.
     */
    public SetEntityStateAsAddedAndResetId(id?: number) {
        if (this.EntityState !== 'Unchanged') {
            throw new Error('Changing a modified object to Added is not supported.');
        }
        this.__selfEntityState = 'Added';
        if (id !== undefined) {
            this.__id = id;
        }
        this.__entityStateHandler.entityStateChanged(this.__selfEntityState);
    }

    /**
     * Creates an instance of RelayTeamContestant from the supplied values.
     * Missing values have default values
     * @param values The values you want to set
     * @returns An object instance with the supplied values set
     */
    public static FromJsValues(values: {
        ID?: number;
        Birthday?: Date | null;
        Chip1?: string | null;
        Chip2?: string | null;
        ContestantID?: number;
        Email?: string | null;
        EventID?: number;
        Firstname?: string | null;
        Gender?: Gender | null;
        LegNumber?: number | null;
        ParticipantID?: number | null;
        ParticipantIDs?: ParticipantID[];
        Surname?: string | null;
        EntityState?: 'Added' | 'Unchanged';
    }): RelayTeamContestant {
        return new RelayTeamContestant(values);
    }

    public static _FromServerJson(json: any): RelayTeamContestant {
        return new RelayTeamContestant({
            ID: json.ID,
            Age: json.Age ?? null,
            Birthday: json.Birthday ? (json.Birthday instanceof Date ? json.Birthday : new Date(json.Birthday + ' 00:00:00')) : null,
            Chip1: json.Chip1 ?? '',
            Chip2: json.Chip2 ?? '',
            ContestantID: json.ContestantID,
            Email: json.Email ?? '',
            EventID: json.EventID,
            Firstname: json.Firstname ?? '',
            Gender: json.Gender == null ? null : json.Gender == 0 ? 'm' : json.Gender == 1 ? 'f' : json.Gender == 2 ? 'n' : null,
            LegNumber: json.LegNumber ?? null,
            ParticipantID: json.ParticipantID ?? null,
            ParticipantIDs: json.ParticipantIDs?.map((x: any) => ParticipantID._FromServerJson(x)),
            Surname: json.Surname ?? '',
            EntityState: 'Unchanged',
        });
    }

    /**
     * Creates an instance of RelayTeamContestant from another instance of RelayTeamContestant.
     * @param other The RelayTeamContestant you want to clone
     * @returns A cloned RelayTeamContestant
     */
    public static FromOther(other: RelayTeamContestant): RelayTeamContestant {
        if (other.EntityState !== 'Unchanged') {
            throw new Error('Cloning an object with EntityState other than Unchanged is not supported');
        }
        return RelayTeamContestant._FromServerJson(JSON.parse(other.serialize()));
    }

    /**
     * Deepclones the object.
     */
    public DeepClone() {
        return RelayTeamContestant.FromOther(this);
    }

    /**
     * IsPropertyChanged.
     */
    public IsPropertyChanged(
        ...props: (
            | 'ID'
            | 'Age'
            | 'Birthday'
            | 'Chip1'
            | 'Chip2'
            | 'ContestantID'
            | 'Email'
            | 'EventID'
            | 'Firstname'
            | 'Gender'
            | 'LegNumber'
            | 'ParticipantID'
            | 'ParticipantIDs'
            | 'Surname'
        )[]
    ) {
        return this.__entityStateHandler.isPropertyChanged(...props);
    }
    static NAME_OF_CLASS = 'RelayTeamContestant';
}
